import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import { useAuth } from './AuthContext';

interface SystemSettings {
  defaultCurrency: string;
  defaultTaxRate: number;
  inventoryThreshold: number;
  orderNumberPrefix: string;
  invoiceNumberPrefix: string;
  autoBackup: boolean;
  backupFrequency: string;
  dataRetentionPeriod: number;
  enableAuditLog: boolean;
}

interface SettingsContextType {
  settings: SystemSettings;
  isLoading: boolean;
  refreshSettings: () => Promise<void>;
  formatCurrency: (amount: number | string) => string;
  getCurrencySymbol: () => string;
  updateSettings: (newSettings: Partial<SystemSettings>) => Promise<void>;
}

const defaultSettings: SystemSettings = {
  defaultCurrency: 'USD',
  defaultTaxRate: 0,
  inventoryThreshold: 10,
  orderNumberPrefix: 'ORD',
  invoiceNumberPrefix: 'INV',
  autoBackup: true,
  backupFrequency: 'daily',
  dataRetentionPeriod: 365,
  enableAuditLog: true,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/settings/system');
      if (response.data.success) {
        setSettings({ ...defaultSettings, ...response.data.data });
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
      // Use default settings if API fails
      setSettings(defaultSettings);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (newSettings: Partial<SystemSettings>) => {
    try {
      const response = await api.put('/settings/system', newSettings);
      if (response.data.success) {
        setSettings(prev => ({ ...prev, ...newSettings }));
      }
    } catch (error) {
      console.error('Failed to update system settings:', error);
      throw error;
    }
  };

  const refreshSettings = async () => {
    await fetchSettings();
  };

  // Currency helper functions using current settings
  const formatCurrencyWithSettings = (amount: number | string): string => {
    return formatCurrency(amount, settings.defaultCurrency);
  };

  const getCurrencySymbolWithSettings = (): string => {
    return getCurrencySymbol(settings.defaultCurrency);
  };

  useEffect(() => {
    // Only fetch settings when user is authenticated and auth loading is complete
    if (!authLoading && isAuthenticated) {
      fetchSettings();
    }
  }, [isAuthenticated, authLoading]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        isLoading,
        refreshSettings,
        formatCurrency: formatCurrencyWithSettings,
        getCurrencySymbol: getCurrencySymbolWithSettings,
        updateSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};